import { useState, useEffect } from "react";
import { useNavigate, useParams, Link, useSearchParams } from "react-router-dom";
import { db } from "../../lib/firebase";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { useAuthContext } from "../../contexts/AuthContext";
import { 
  AcademicCapIcon, 
  PhotoIcon, 
  CurrencyDollarIcon,
  CheckCircleIcon,
  ArrowLeftIcon,
  BookOpenIcon
} from "../../components/icons/OutlineIcons";
import { CheckCircle, Eye, Ban, Star, AlertTriangle, ShieldAlert, Trash2 } from "lucide-react";
import MediaUpload from "../../components/MediaUpload";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useSubscription } from "../../hooks/useSubscription";

export default function EditCourse() {
  const { id } = useParams();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuthContext();

  const [course, setCourse] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("basic");
  const [thumbnailUploaded, setThumbnailUploaded] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  const { tier } = useSubscription();
  const canDeleteCourse = tier.includes('pro_') || tier.includes('pro_plus');
  
  const isAdmin = searchParams.get("admin") === "true" && (user?.email === "viitamint@gmail.com" || user?.role === "admin");

  useEffect(() => {
    async function load() {
      if (!id) return;
      const snap = await getDoc(doc(db, "courses", id));
      if (snap.exists()) {
        const courseData = snap.data();
        // Check if user is owner or admin
        if (!isAdmin && courseData.instructorId !== user?.uid) {
          alert("คุณไม่มีสิทธิ์แก้ไขคอร์สนี้");
          nav("/instructor/my-courses");
          return;
        }
        setCourse(courseData);
      }
      setLoading(false);
    }
    load();
  }, [id, isAdmin, user, nav]);

  const handleDeleteCourse = async () => {
    if (!id || !user) return;

    setIsDeleting(true);
    try {
      const functions = getFunctions(undefined, 'us-central1');
      const deleteCourse = httpsCallable(functions, 'deleteCourse');
      
      const result = await deleteCourse({ courseId: id });
      const data = result.data as any;

      console.log('[EditCourse] Delete result:', data);

      // Show success message
      alert(data.message || 'ลบคอร์สสำเร็จ!');

      // Navigate back to my courses
      nav('/instructor/my-courses');
    } catch (error: any) {
      console.error('[EditCourse] Delete error:', error);
      alert('เกิดข้อผิดพลาดในการลบคอร์ส: ' + (error.message || 'Unknown error'));
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const save = async () => {
    if (!id) return;

    setSaving(true);
    try {
      console.log("Saving course with price:", course.price, typeof course.price);
      await updateDoc(doc(db, "courses", id), course);
      
      // Show success popup with fade-out animation
      const successMsg = document.createElement('div');
      successMsg.className = 'fixed top-20 left-1/2 -translate-x-1/2 bg-gradient-to-r from-green-500 to-emerald-500 text-white px-5 py-3 rounded-lg shadow-2xl z-50 flex items-center gap-2 text-sm font-semibold';
      successMsg.style.transition = 'opacity 0.5s ease-out, transform 0.5s ease-out';
      successMsg.style.opacity = '1';
      successMsg.style.transform = 'translateY(0)';
      successMsg.innerHTML = `
        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
        </svg>
        <span>บันทึกสำเร็จ!</span>
      `;
      document.body.appendChild(successMsg);
      
      // Fade out after 2.5 seconds
      setTimeout(() => {
        successMsg.style.opacity = '0';
        successMsg.style.transform = 'translateY(-20px)';
      }, 2500);
      
      // Remove from DOM after animation completes
      setTimeout(() => successMsg.remove(), 3000);
    } catch (error) {
      console.error("Error saving course:", error);
      alert("เกิดข้อผิดพลาดในการบันทึก");
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-purple-50/20 to-pink-50/20 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-textLight text-lg">กำลังโหลดข้อมูลคอร์ส...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-purple-50/20 to-pink-50/20 py-4 md:py-8 px-3 md:px-4">
      <div className="max-w-6xl mx-auto">
        {/* Admin Warning Banner */}
        {isAdmin && (
          <div className="bg-gradient-to-r from-red-500 to-blue-500 text-white rounded-xl md:rounded-2xl p-3 md:p-4 mb-4 md:mb-6 shadow-xl">
            <div className="flex items-center gap-2 md:gap-3">
              <div className="w-10 md:w-12 h-10 md:h-12 bg-white/20 rounded-lg md:rounded-xl flex items-center justify-center">
                <span className="text-xl md:text-2xl">⚠️</span>
              </div>
              <div>
                <h3 className="font-bold text-sm md:text-lg">โหมด Admin</h3>
                <p className="text-xs md:text-sm text-white/90">คุณกำลังแก้ไขคอร์สในฐานะผู้ดูแลระบบ การเปลี่ยนแปลงจะมีผลทันที</p>
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="mb-4 md:mb-8">
          <button
            onClick={() => nav(-1)}
            className="flex items-center gap-1.5 md:gap-2 text-textLight hover:text-textDark text-sm md:text-base transition-colors mb-3 md:mb-4"
          >
            <ArrowLeftIcon className="w-4 md:w-5 h-4 md:h-5" />
            กลับ
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl md:text-4xl font-bold text-textDark mb-1 md:mb-2">แก้ไขคอร์ส</h1>
              <p className="text-textLight text-sm md:text-lg hidden md:block">ปรับปรุงและจัดการคอร์สของคุณ</p>
            </div>
            <Link
              to={`/instructor/courses/${id}/lessons`}
              className="flex items-center gap-1.5 md:gap-2 bg-gradient-primary text-white px-3 md:px-6 py-2 md:py-3 rounded-lg md:rounded-xl text-xs md:text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
            >
              <BookOpenIcon className="w-4 md:w-5 h-4 md:h-5" />
              <span className="hidden sm:inline">จัดการบทเรียน</span>
              <span className="sm:hidden">บทเรียน</span>
            </Link>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl md:rounded-2xl shadow-lg mb-4 md:mb-6 p-1.5 md:p-2">
          <div className="flex gap-1.5 md:gap-2 flex-wrap">
            <button
              onClick={() => setActiveTab("basic")}
              className={`flex-1 px-2 md:px-6 py-2 md:py-3 rounded-lg md:rounded-xl text-xs md:text-base font-semibold transition-all duration-300 ${
                activeTab === "basic"
                  ? "bg-gradient-primary text-white shadow-md"
                  : "text-textLight hover:bg-gray-50"
              }`}
            >
              <AcademicCapIcon className="w-4 md:w-5 h-4 md:h-5 inline-block mr-1 md:mr-2" />
              <span className="hidden sm:inline">ข้อมูลพื้นฐาน</span>
              <span className="sm:hidden">ข้อมูล</span>
            </button>
            <button
              onClick={() => setActiveTab("media")}
              className={`flex-1 px-2 md:px-6 py-2 md:py-3 rounded-lg md:rounded-xl text-xs md:text-base font-semibold transition-all duration-300 ${
                activeTab === "media"
                  ? "bg-gradient-primary text-white shadow-md"
                  : "text-textLight hover:bg-gray-50"
              }`}
            >
              <PhotoIcon className="w-4 md:w-5 h-4 md:h-5 inline-block mr-1 md:mr-2" />
              <span className="hidden sm:inline">รูปภาพและสื่อ</span>
              <span className="sm:hidden">รูปภาพ</span>
            </button>
            <button
              onClick={() => setActiveTab("pricing")}
              className={`flex-1 px-2 md:px-6 py-2 md:py-3 rounded-lg md:rounded-xl text-xs md:text-base font-semibold transition-all duration-300 ${
                activeTab === "pricing"
                  ? "bg-gradient-primary text-white shadow-md"
                  : "text-textLight hover:bg-gray-50"
              }`}
            >
              <CurrencyDollarIcon className="w-4 md:w-5 h-4 md:h-5 inline-block mr-1 md:mr-2" />
              <span className="hidden sm:inline">ราคาและการขาย</span>
              <span className="sm:hidden">ราคา</span>
            </button>
            {isAdmin && (
              <button
                onClick={() => setActiveTab("admin")}
                className={`flex-1 px-2 md:px-6 py-2 md:py-3 rounded-lg md:rounded-xl text-xs md:text-base font-semibold transition-all duration-300 ${
                  activeTab === "admin"
                    ? "bg-gradient-to-r from-red-500 to-blue-500 text-white shadow-md"
                    : "text-red-600 hover:bg-red-50"
                }`}
              >
                <CheckCircleIcon className="w-4 md:w-5 h-4 md:h-5 inline-block mr-1 md:mr-2" />
                Admin
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-2xl md:rounded-3xl shadow-xl p-4 md:p-8 lg:p-12">
          {/* Basic Info Tab */}
          {activeTab === "basic" && (
            <div className="space-y-4 md:space-y-6 animate-fadeIn">
              <div className="flex items-center gap-2 md:gap-3 mb-4 md:mb-6">
                <div className="bg-blue-100 p-2 md:p-3 rounded-lg md:rounded-xl">
                  <AcademicCapIcon className="w-6 md:w-8 h-6 md:h-8 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-lg md:text-2xl font-bold text-textDark">ข้อมูลพื้นฐาน</h2>
                  <p className="text-xs md:text-base text-textLight">แก้ไขชื่อและคำอธิบายคอร์ส</p>
                </div>
              </div>

              <div>
                <label className="block text-xs md:text-sm font-bold text-textDark mb-1.5 md:mb-2">
                  ชื่อคอร์ส *
                </label>
                <input
                  type="text"
                  value={course.title || ""}
                  onChange={(e) => setCourse({ ...course, title: e.target.value })}
                  placeholder="เช่น: เรียนรู้ React และ TypeScript สำหรับมือใหม่"
                  className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg md:rounded-xl border-2 border-gray-200 focus:border-primary focus:outline-none transition-colors text-sm md:text-lg"
                />
              </div>

              <div>
                <label className="block text-xs md:text-sm font-bold text-textDark mb-1.5 md:mb-2">
                  คำอธิบายคอร์ส *
                </label>
                <textarea
                  value={course.description || ""}
                  onChange={(e) => setCourse({ ...course, description: e.target.value })}
                  placeholder="อธิบายว่าคอร์สนี้สอนอะไร นักเรียนจะได้เรียนรู้อะไรบ้าง และเหมาะกับใคร"
                  rows={6}
                  className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg md:rounded-xl border-2 border-gray-200 focus:border-primary focus:outline-none transition-colors resize-none text-sm md:text-base"
                />
                <p className="text-xs md:text-sm text-textLight mt-1.5 md:mt-2">
                  {(course.description || "").length} ตัวอักษร
                </p>
              </div>

              {course.category && (
                <div>
                  <label className="block text-sm font-bold text-textDark mb-2">
                    หมวดหมู่
                  </label>
                  <div className="px-4 py-3 rounded-xl border-2 border-gray-200 bg-gray-50 text-textDark">
                    {course.category}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Media Tab */}
          {activeTab === "media" && (
            <div className="space-y-6 animate-fadeIn">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-green-100 p-3 rounded-xl">
                  <PhotoIcon className="w-8 h-8 text-green-600" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-textDark">รูปภาพและสื่อ</h2>
                  <p className="text-textLight">จัดการรูปภาพปกและสื่อต่างๆ</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-textDark mb-2">
                  รูปภาพปกคอร์ส *
                </label>
                {!course.thumbnail || !thumbnailUploaded ? (
                  <MediaUpload
                    courseId={id!}
                    mediaType="image"
                    onUploadComplete={(mediaId, url) => {
                      setCourse({ ...course, thumbnail: url });
                      setThumbnailUploaded(true);
                    }}
                    onUploadError={(error) => alert(error)}
                    aspectRatio={16 / 9}
                  />
                ) : (
                  <div className="space-y-3">
                    <div className="p-4 bg-green-50 border-2 border-green-200 rounded-xl">
                      <div className="flex items-center gap-2 text-green-700">
                        <CheckCircleIcon className="w-5 h-5" />
                        <span className="font-semibold">อัปโหลดรูปภาพสำเร็จ!</span>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setCourse({ ...course, thumbnail: "" });
                        setThumbnailUploaded(false);
                      }}
                      className="text-sm text-blue-600 hover:text-blue-700 font-semibold"
                    >
                      อัปโหลดรูปภาพใหม่
                    </button>
                  </div>
                )}
                <p className="text-sm text-textLight mt-2">
                  แนะนำขนาด 1280x720 พิกเซล (16:9) • รองรับ .jpg, .png, .webp
                </p>
              </div>

              {course.thumbnail && (
                <div>
                  <p className="text-sm font-bold text-textDark mb-3">ตัวอย่างรูปภาพปก:</p>
                  <div className="relative rounded-2xl overflow-hidden border-2 border-gray-200 shadow-lg">
                    <img
                      src={course.thumbnail}
                      alt="Course thumbnail"
                      className="w-full h-64 object-cover"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        const parent = e.currentTarget.parentElement;
                        if (parent) {
                          const errorDiv = document.createElement('div');
                          errorDiv.className = 'w-full h-64 bg-gray-100 flex items-center justify-center text-gray-500';
                          errorDiv.textContent = 'ไม่สามารถโหลดรูปภาพได้';
                          parent.appendChild(errorDiv);
                        }
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent flex items-end p-6">
                      <div className="text-white">
                        <h3 className="text-2xl font-bold mb-1">{course.title}</h3>
                        <p className="text-sm opacity-90">ตัวอย่างการแสดงผล</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Pricing Tab */}
          {activeTab === "pricing" && (
            <div className="space-y-6 animate-fadeIn">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-blue-100 p-3 rounded-xl">
                  <CurrencyDollarIcon className="w-8 h-8 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-textDark">ราคาและการขาย</h2>
                  <p className="text-textLight">กำหนดราคาและตัวเลือกการขาย</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-textDark mb-2">
                  ราคาคอร์ส (บาท) *
                </label>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2">
                    <CurrencyDollarIcon className="w-6 h-6 text-gray-400" />
                  </div>
                  <input
                    type="number"
                    value={course.price || 0}
                    onChange={(e) => setCourse({ ...course, price: parseInt(e.target.value) || 0 })}
                    placeholder="0"
                    min="0"
                    step="1"
                    className="w-full pl-12 pr-4 py-3 rounded-xl border-2 border-gray-200 focus:border-primary focus:outline-none transition-colors text-lg"
                  />
                </div>
                <p className="text-sm text-textLight mt-2">
                  ใส่ 0 หากต้องการให้คอร์สฟรี
                </p>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 border-2 border-blue-100">
                <h3 className="font-bold text-textDark mb-3 flex items-center gap-2">
                  <span className="text-2xl">💰</span>
                  เคล็ดลับการตั้งราคา
                </h3>
                <ul className="space-y-2 text-sm text-textLight">
                  <li>• วิเคราะห์ราคาคอร์สที่คล้ายกันในตลาด</li>
                  <li>• พิจารณาความยาวและคุณภาพของเนื้อหา</li>
                  <li>• เริ่มต้นด้วยราคาที่เหมาะสม แล้วค่อยปรับตามผลตอบรับ</li>
                  <li>• คอร์สฟรีช่วยดึงดูดนักเรียนและสร้างชื่อเสียง</li>
                </ul>
              </div>
            </div>
          )}

          {/* Admin Tab */}
          {activeTab === "admin" && isAdmin && (
            <div className="space-y-6 animate-fadeIn">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-red-100 p-3 rounded-xl">
                  <ShieldAlert className="w-8 h-8 text-red-600" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-textDark">การจัดการขั้นสูง</h2>
                  <p className="text-textLight">ฟีเจอร์สำหรับผู้ดูแลระบบเท่านั้น</p>
                </div>
              </div>

              {/* Course Status */}
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-6 border-2 border-blue-200">
                <h3 className="text-lg font-bold text-textDark mb-4">สถานะคอร์ส</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <button
                    onClick={() => setCourse({...course, status: "published"})}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      course.status === "published" || !course.status
                        ? "border-green-500 bg-green-50"
                        : "border-gray-200 hover:border-green-300"
                    }`}
                  >
                    <div className="text-center">
                      <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-600" />
                      <div className="font-bold text-textDark">เผยแพร่</div>
                      <div className="text-xs text-textLight mt-1">แสดงในหน้าแรก</div>
                    </div>
                  </button>

                  <button
                    onClick={() => setCourse({...course, status: "hidden"})}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      course.status === "hidden"
                        ? "border-yellow-500 bg-yellow-50"
                        : "border-gray-200 hover:border-yellow-300"
                    }`}
                  >
                    <div className="text-center">
                      <Eye className="w-12 h-12 mx-auto mb-2 text-yellow-600" />
                      <div className="font-bold text-textDark">ซ่อน</div>
                      <div className="text-xs text-textLight mt-1">เข้าถึงผ่าน URL เท่านั้น</div>
                    </div>
                  </button>

                  <button
                    onClick={() => setCourse({...course, status: "suspended"})}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      course.status === "suspended"
                        ? "border-red-500 bg-red-50"
                        : "border-gray-200 hover:border-red-300"
                    }`}
                  >
                    <div className="text-center">
                      <Ban className="w-12 h-12 mx-auto mb-2 text-red-600" />
                      <div className="font-bold text-textDark">ระงับ</div>
                      <div className="text-xs text-textLight mt-1">ไม่สามารถเข้าถึงได้</div>
                    </div>
                  </button>
                </div>
              </div>

              {/* Featured Course */}
              <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-2xl p-6 border-2 border-yellow-200">
                <h3 className="text-lg font-bold text-textDark mb-4">คอร์สแนะนำ</h3>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-textDark font-semibold mb-1">แสดงในหน้าแรก</p>
                    <p className="text-sm text-textLight">คอร์สนี้จะปรากฏในส่วนคอร์สแนะนำ</p>
                  </div>
                  <button
                    onClick={() => setCourse({...course, featured: !course.featured})}
                    className={`px-6 py-3 rounded-xl font-bold transition-all ${
                      course.featured
                        ? "bg-gradient-to-r from-yellow-500 to-blue-500 text-white shadow-lg"
                        : "bg-white text-gray-600 border-2 border-gray-300"
                    }`}
                  >
                    {course.featured ? (
                      <span className="flex items-center gap-2">
                        <Star className="w-5 h-5" />
                        แนะนำอยู่
                      </span>
                    ) : (
                      "ตั้งเป็นแนะนำ"
                    )}
                  </button>
                </div>
              </div>

              {/* Course Priority */}
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-2xl p-6 border-2 border-blue-200">
                <h3 className="text-lg font-bold text-textDark mb-4">ลำดับความสำคัญ</h3>
                <div className="flex items-center gap-4">
                  <label className="text-textDark font-semibold">Priority:</label>
                  <input
                    type="number"
                    value={course.priority || 0}
                    onChange={(e) => setCourse({...course, priority: parseInt(e.target.value)})}
                    className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                    min="0"
                    max="100"
                  />
                  <span className="text-sm text-textLight">ยิ่งสูงยิ่งแสดงก่อน</span>
                </div>
              </div>

              {/* Admin Notes */}
              <div className="bg-gradient-to-r from-gray-50 to-slate-50 rounded-2xl p-6 border-2 border-gray-200">
                <h3 className="text-lg font-bold text-textDark mb-4">บันทึกของ Admin</h3>
                <textarea
                  value={course.adminNotes || ""}
                  onChange={(e) => setCourse({...course, adminNotes: e.target.value})}
                  placeholder="บันทึกข้อมูลสำหรับ Admin (นักเรียนและผู้สอนจะไม่เห็น)"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none resize-none"
                  rows={4}
                />
              </div>

              {/* Danger Zone */}
              <div className="bg-gradient-to-r from-red-50 to-pink-50 rounded-2xl p-6 border-2 border-red-300">
                <h3 className="text-lg font-bold text-red-600 mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" />
                  Danger Zone
                </h3>
                <div className="space-y-3">
                  <button
                    onClick={() => {
                      if (window.confirm("คุณแน่ใจหรือไม่ว่าต้องการระงับคอร์สนี้?")) {
                        setCourse({...course, status: "suspended"});
                        save();
                      }
                    }}
                    className="w-full bg-red-500 hover:bg-red-600 text-white px-6 py-3 rounded-xl font-bold transition-all"
                  >
                    <span className="flex items-center justify-center gap-2">
                      <Ban className="w-5 h-5" />
                      ระงับคอร์สทันที
                    </span>
                  </button>
                  <p className="text-xs text-red-600 text-center">
                    การระงับจะทำให้นักเรียนไม่สามารถเข้าถึงคอร์สได้
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Save Button */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t-2 border-gray-100">
            <div className="flex gap-3">
              <button
                onClick={() => nav(-1)}
                className="px-6 py-3 rounded-xl font-semibold text-textDark bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                ยกเลิก
              </button>
              {canDeleteCourse && (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="px-6 py-3 rounded-xl font-semibold text-white bg-red-500 hover:bg-red-600 transition-colors flex items-center gap-2"
                >
                  <Trash2 className="w-5 h-5" />
                  ลบคอร์ส
                </button>
              )}
            </div>
            <button
              onClick={save}
              disabled={saving}
              className="px-8 py-3 rounded-xl font-semibold text-white bg-gradient-primary hover:shadow-xl transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center gap-2"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  กำลังบันทึก...
                </>
              ) : (
                <>
                  <CheckCircleIcon className="w-5 h-5" />
                  บันทึกการเปลี่ยนแปลง
                </>
              )}
            </button>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-8 grid md:grid-cols-2 gap-6">
          <Link
            to={`/instructor/courses/${id}/lessons`}
            className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 group"
          >
            <div className="flex items-center gap-4">
              <div className="bg-blue-100 p-4 rounded-xl group-hover:scale-110 transition-transform">
                <BookOpenIcon className="w-8 h-8 text-blue-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-textDark mb-1 group-hover:text-primary transition-colors">
                  จัดการบทเรียน
                </h3>
                <p className="text-sm text-textLight">เพิ่ม แก้ไข หรือลบบทเรียน</p>
              </div>
            </div>
          </Link>

          <button
            onClick={() => window.open(`/courses/${id}`, '_blank')}
            className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 group text-left"
          >
            <div className="flex items-center gap-4">
              <div className="bg-blue-100 p-4 rounded-xl group-hover:scale-110 transition-transform">
                <AcademicCapIcon className="w-8 h-8 text-blue-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-textDark mb-1 group-hover:text-primary transition-colors">
                  ดูตัวอย่างคอร์ส
                </h3>
                <p className="text-sm text-textLight">ดูว่านักเรียนจะเห็นอย่างไร</p>
              </div>
            </div>
          </button>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 md:p-8">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-textDark mb-2">
                  ยืนยันการลบคอร์ส
                </h3>
                <p className="text-sm text-textLight">
                  คุณแน่ใจหรือไม่ที่จะลบคอร์ส <span className="font-semibold text-textDark">"{course?.title}"</span>?
                </p>
              </div>
            </div>

            <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 mb-6">
              <p className="text-sm text-red-800 font-medium mb-2">
                ⚠️ การลบคอร์สจะทำให้:
              </p>
              <ul className="text-sm text-red-700 space-y-1 ml-4">
                <li>• ลบบทเรียนทั้งหมดในคอร์ส</li>
                <li>• ลบไฟล์และวิดีโอที่อัปโหลดทั้งหมด</li>
                <li>• คืนพื้นที่จัดเก็บข้อมูล</li>
                <li>• <strong>ไม่สามารถกู้คืนได้</strong></li>
              </ul>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
                className="flex-1 px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                ยกเลิก
              </button>
              <button
                onClick={handleDeleteCourse}
                disabled={isDeleting}
                className="flex-1 px-4 py-3 bg-red-500 hover:bg-red-600 text-white rounded-xl font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isDeleting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    กำลังลบ...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    ลบคอร์ส
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
